import axios from 'axios';
import { useState, useRef, useEffect } from 'react';
import { Stage, Layer, Line, Circle, Rect, RegularPolygon, Text as KonvaText, Image as KonvaImage, Transformer } from 'react-konva';
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Modal from 'react-modal';
import { X, Undo, Redo, Pencil, Trash2, Type, Square, CircleDot, Triangle as TriangleIcon } from 'lucide-react';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

// Configuration de react-modal
Modal.setAppElement('#root');

const OUTILS = {
  CRAYON: 'crayon',
  CERCLE: 'cercle',
  RECTANGLE: 'rectangle',
  CARRE: 'carre',         // 🆕 Phase D2
  TRIANGLE: 'triangle',   // 🆕 Phase D2
  TEXTE: 'texte',
  SELECTION: 'selection', // 🆕 Phase D2 — pour drag/resize via Transformer
  GOMME: 'gomme'
};

const COULEURS = [
  { nom: 'Rouge', hex: '#ef4444' },
  { nom: 'Bleu', hex: '#3b82f6' },
  { nom: 'Vert', hex: '#10b981' },
  { nom: 'Jaune', hex: '#f59e0b' },
  { nom: 'Noir', hex: '#000000' }
];

export function EditeurPhotoZone({ photoUrl, photoUrlOriginal, bonId, dossierId, onClose, onZoneCreated }) {
  const [outil, setOutil] = useState(OUTILS.CRAYON);
  const [couleur, setCouleur] = useState('#ef4444');
  const [zoneSelectionnee, setZoneSelectionnee] = useState(null);
  const [zoneNom, setZoneNom] = useState(''); // 🆕 [5.1.A] nom libre éditable à la création
  const [zoneDescription, setZoneDescription] = useState('');
  const [zonesExistantes, setZonesExistantes] = useState([]);
  // 🛠️ [Phase 21 R1] Numéros déjà pris sur ce BT (pour griser les palettes)
  const [numerosPris, setNumerosPris] = useState([]);
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState(null);
  const [lines, setLines] = useState([]);
  const [shapes, setShapes] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyStep, setHistoryStep] = useState(0);
  const [isDrawing, setIsDrawing] = useState(false);
  // 🆕 Phase D2 — sélection d'une forme pour redim via Konva Transformer
  const [selectedShapeId, setSelectedShapeId] = useState(null);
  const stageRef = useRef(null);
  const transformerRef = useRef(null);
  const shapeRefs = useRef({}); // map id → Konva node, alimenté via ref callback
  const canvasWidth = 900;
  const canvasHeight = 600;

  // 🆕 Phase D2 — attacher le Transformer au shape sélectionné quand il change
  useEffect(() => {
    const tr = transformerRef.current;
    if (!tr) return;
    if (selectedShapeId && shapeRefs.current[selectedShapeId]) {
      tr.nodes([shapeRefs.current[selectedShapeId]]);
      tr.getLayer()?.batchDraw();
    } else {
      tr.nodes([]);
      tr.getLayer()?.batchDraw();
    }
  }, [selectedShapeId, shapes]);

  // Charger l'image et les zones existantes
  useEffect(() => {
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.src = photoUrl;
    img.onload = () => {
      setImage(img);
    };

    fetchZonesExistantes();
  }, [photoUrl, bonId]);

  const fetchZonesExistantes = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/crm/bons-travail/${bonId}`);
      if (res.status === 200 && res.data.zones) {
        const zones = res.data.zones || [];
        const zonesNoms = zones.map(z => z.nom).filter(Boolean);
        // 🛠️ [Phase 21 R1] On stocke aussi les NUMÉROS pris : le check anti-doublon
        //   doit porter sur le `numero` (palette 1-20) et pas uniquement sur le nom,
        //   sinon des zones renommées laissent des numéros libres en apparence.
        const nums = zones.map(z => z.numero).filter(n => Number.isInteger(n));
        setZonesExistantes(zonesNoms);
        setNumerosPris(nums);
      }
    } catch (err) {
      console.error("Erreur chargement zones:", err);
    }
  };

  const handleMouseDown = (e) => {
    if (outil === OUTILS.GOMME) return; // Gomme se fait sur les objets sélectionnés
    // 🆕 Phase D2 — en mode SELECTION, click sur le fond (Stage ou Image) déselectionne.
    if (outil === OUTILS.SELECTION) {
      const target = e.target;
      // clickedOnEmpty si on clique le Stage lui-même ou l'Image de fond
      const clickedOnEmpty = target === target.getStage() || target.attrs?.image;
      if (clickedOnEmpty) setSelectedShapeId(null);
      return;
    }

    const pos = e.target.getStage().getPointerPosition();
    setIsDrawing(true);

    if (outil === OUTILS.CRAYON) {
      setLines([...lines, { points: [pos.x, pos.y], color: couleur, tool: 'pen' }]);
    } else if (outil === OUTILS.CERCLE) {
      setShapes([...shapes, { type: 'circle', x: pos.x, y: pos.y, radius: 50, color: couleur, id: Date.now() }]);
      setIsDrawing(false);
    } else if (outil === OUTILS.RECTANGLE) {
      setShapes([...shapes, { type: 'rect', x: pos.x, y: pos.y, width: 100, height: 60, color: couleur, id: Date.now() }]);
      setIsDrawing(false);
    } else if (outil === OUTILS.CARRE) {
      // 🆕 Phase D2 — carré = rect avec width=height au démarrage (libre au resize)
      setShapes([...shapes, { type: 'square', x: pos.x, y: pos.y, width: 80, height: 80, color: couleur, id: Date.now() }]);
      setIsDrawing(false);
    } else if (outil === OUTILS.TRIANGLE) {
      // 🆕 Phase D2 — triangle équilatéral via RegularPolygon
      setShapes([...shapes, { type: 'triangle', x: pos.x, y: pos.y, radius: 50, color: couleur, id: Date.now() }]);
      setIsDrawing(false);
    } else if (outil === OUTILS.TEXTE) {
      setShapes([...shapes, { type: 'text', x: pos.x, y: pos.y, text: 'Texte', color: couleur, id: Date.now() }]);
      setIsDrawing(false);
    }
  };

  const handleMouseMove = (e) => {
    if (!isDrawing || outil !== OUTILS.CRAYON) return;
    const stage = e.target.getStage();
    const point = stage.getPointerPosition();
    let lastLine = lines[lines.length - 1];
    lastLine.points = lastLine.points.concat([point.x, point.y]);
    lines.splice(lines.length - 1, 1, lastLine);
    setLines(lines.concat());
  };

  const handleMouseUp = () => {
    setIsDrawing(false);
    saveHistory();
  };

  const saveHistory = () => {
    const currentState = { lines: [...lines], shapes: [...shapes] };
    const newHistory = history.slice(0, historyStep + 1);
    setHistory([...newHistory, currentState]);
    setHistoryStep(newHistory.length);
  };

  const handleUndo = () => {
    if (historyStep === 0) return;
    const newStep = historyStep - 1;
    setHistoryStep(newStep);
    const state = history[newStep];
    if (state) {
      setLines(state.lines || []);
      setShapes(state.shapes || []);
    }
  };

  const handleRedo = () => {
    if (historyStep >= history.length - 1) return;
    const newStep = historyStep + 1;
    setHistoryStep(newStep);
    const state = history[newStep];
    if (state) {
      setLines(state.lines || []);
      setShapes(state.shapes || []);
    }
  };

  const handleDeleteShape = (id) => {
    setShapes(shapes.filter(s => s.id !== id));
    if (selectedShapeId === id) setSelectedShapeId(null);
    saveHistory();
  };

  // 🆕 Phase D2 — Click sur une forme :
  //  • outil GOMME → suppression
  //  • outil SELECTION (ou autre) → sélection pour redim/drag via Transformer
  const handleShapeClick = (id) => {
    if (outil === OUTILS.GOMME) {
      handleDeleteShape(id);
    } else {
      setSelectedShapeId(id);
    }
  };

  // 🆕 Phase D2 — fin du transform (resize ou drag) : persister la nouvelle taille/position
  const handleShapeTransformEnd = (id) => {
    const node = shapeRefs.current[id];
    if (!node) return;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    // Konva applique scale lors du resize ; on convertit en width/height/radius
    // pour rester normalisé puis on remet scale à 1.
    setShapes((prev) => prev.map((s) => {
      if (s.id !== id) return s;
      const updated = { ...s, x: node.x(), y: node.y() };
      if (s.type === 'rect' || s.type === 'square') {
        updated.width = Math.max(10, (s.width || 100) * scaleX);
        updated.height = Math.max(10, (s.height || 60) * scaleY);
      } else if (s.type === 'circle' || s.type === 'triangle') {
        // pour cercle/triangle, on prend le facteur moyen pour conserver la forme
        const factor = (Math.abs(scaleX) + Math.abs(scaleY)) / 2;
        updated.radius = Math.max(10, (s.radius || 50) * factor);
      } else if (s.type === 'text') {
        updated.fontSize = Math.max(8, (s.fontSize || 24) * Math.abs(scaleY));
      }
      return updated;
    }));
    node.scaleX(1);
    node.scaleY(1);
    saveHistory();
  };

  const handleCreerZone = async () => {
    if (!zoneSelectionnee) {
      toast.error("Veuillez sélectionner un numéro de zone");
      return;
    }

    setLoading(true);
    try {
      // Exporter le canvas en image
      const stage = stageRef.current;
      const dataURL = stage.toDataURL({ pixelRatio: 2 });

      // Upload de l'image annotée
      const blob = await (await fetch(dataURL)).blob();
      const formData = new FormData();
      formData.append('file', blob, `zone_${zoneSelectionnee}_annotee.png`);

      const uploadRes = await axios.post(`${API_URL}/api/crm/upload-photo`, formData);
      const photoAnnoteeUrl = uploadRes.data.photo_url;

      if (!photoAnnoteeUrl) throw new Error("Upload échoué");

      // Créer la zone
      const res = await axios.post(
        `${API_URL}/api/crm/dossiers/${dossierId}/creer-zone-depuis-photo`,
        {
          bon_id: bonId,
          zone_numero: zoneSelectionnee,
          nom: (zoneNom || '').trim() || undefined, // 🆕 [5.1.A] nom libre éditable, fallback "Zone N" côté backend
          photo_annotee_url: photoAnnoteeUrl,
          description: zoneDescription || ''
        }
      );

      if (res.data.status === 'error') {
        toast.error(res.data.message);
        setZonesExistantes(res.data.zones_existantes || []);
      } else {
        toast.success(`Zone ${zoneSelectionnee} créée avec succès !`);
        onZoneCreated();
      }
    } catch (err) {
      console.error("Erreur création zone:", err);
      toast.error("Erreur lors de la création de la zone");
    } finally {
      setLoading(false);
    }
  };

  // 🆕 Phase D3 (2026-05-04) — palette élargie de 10 → 20 zones (décision Eliot).
  // Si besoin futur d'augmenter encore, modifier juste la valeur length ci-dessous.
  const zones = Array.from({ length: 20 }, (_, i) => i + 1);
  // 🛠️ [Phase 21 R1] Check sur le NUMÉRO (palette 1-20) — l'ancien check par nom
  // laissait passer les doublons quand l'utilisateur renommait la zone (ex "Plafond
  // cuisine" sur numero=1 → un 2e clic palette numero=1 passait car le nom comparé
  // ne contenait pas "Zone 1").
  const zoneEstDispo = (num) => !numerosPris.includes(num);

  return (
    <Modal
      isOpen={true}
      onRequestClose={onClose}
      className="bg-white rounded-lg shadow-2xl max-w-6xl w-full mx-auto my-4 p-6 outline-none max-h-[calc(100vh-2rem)] overflow-y-auto"
      overlayClassName="fixed inset-0 bg-black/70 flex items-start justify-center z-50 p-4 overflow-y-auto"
    >
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between border-b pb-3">
          <h2 className="text-2xl font-bold text-slate-800">Créer une zone à partir de photo</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Toolbar */}
        <div className="space-y-3">
          <div>
            <Label className="text-sm font-semibold mb-2 block">🛠️ Outils</Label>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant={outil === OUTILS.CRAYON ? "default" : "outline"}
                onClick={() => setOutil(OUTILS.CRAYON)}
              >
                <Pencil className="w-4 h-4 mr-1" /> Crayon
              </Button>
              <Button
                size="sm"
                variant={outil === OUTILS.CERCLE ? "default" : "outline"}
                onClick={() => setOutil(OUTILS.CERCLE)}
              >
                <CircleDot className="w-4 h-4 mr-1" /> Cercle
              </Button>
              <Button
                size="sm"
                variant={outil === OUTILS.RECTANGLE ? "default" : "outline"}
                onClick={() => setOutil(OUTILS.RECTANGLE)}
              >
                <Square className="w-4 h-4 mr-1" /> Rectangle
              </Button>
              {/* 🆕 Phase D2 — Carré (icône Square stylée fill pour distinguer) */}
              <Button
                size="sm"
                variant={outil === OUTILS.CARRE ? "default" : "outline"}
                onClick={() => setOutil(OUTILS.CARRE)}
                data-testid="outil-carre"
                title="Carré (cliquer puis glisser les poignées pour redimensionner)"
              >
                <Square className="w-4 h-4 mr-1 fill-current" /> Carré
              </Button>
              {/* 🆕 Phase D2 — Triangle */}
              <Button
                size="sm"
                variant={outil === OUTILS.TRIANGLE ? "default" : "outline"}
                onClick={() => setOutil(OUTILS.TRIANGLE)}
                data-testid="outil-triangle"
                title="Triangle (cliquer puis glisser les poignées pour redimensionner)"
              >
                <TriangleIcon className="w-4 h-4 mr-1" /> Triangle
              </Button>
              <Button
                size="sm"
                variant={outil === OUTILS.TEXTE ? "default" : "outline"}
                onClick={() => setOutil(OUTILS.TEXTE)}
              >
                <Type className="w-4 h-4 mr-1" /> Texte
              </Button>
              {/* 🆕 Phase D2 — Sélection (curseur) pour redim/drag les formes existantes */}
              <Button
                size="sm"
                variant={outil === OUTILS.SELECTION ? "default" : "outline"}
                onClick={() => setOutil(OUTILS.SELECTION)}
                data-testid="outil-selection"
                title="Sélectionner une forme pour la redimensionner ou la déplacer"
              >
                ↔️ Sélection
              </Button>
              <Button
                size="sm"
                variant={outil === OUTILS.GOMME ? "default" : "outline"}
                onClick={() => setOutil(OUTILS.GOMME)}
              >
                <Trash2 className="w-4 h-4 mr-1" /> Gomme
              </Button>
              <Button size="sm" variant="outline" onClick={handleUndo} disabled={historyStep === 0}>
                <Undo className="w-4 h-4 mr-1" /> Annuler
              </Button>
              <Button size="sm" variant="outline" onClick={handleRedo} disabled={historyStep >= history.length - 1}>
                <Redo className="w-4 h-4 mr-1" /> Refaire
              </Button>
            </div>
          </div>

          <div>
            <Label className="text-sm font-semibold mb-2 block">🎨 Couleurs</Label>
            <div className="flex gap-2">
              {COULEURS.map((c) => (
                <button
                  key={c.hex}
                  onClick={() => setCouleur(c.hex)}
                  className={`w-10 h-10 rounded-full border-2 transition-transform hover:scale-110 ${
                    couleur === c.hex ? 'border-black scale-110' : 'border-gray-300'
                  }`}
                  style={{ backgroundColor: c.hex }}
                  title={c.nom}
                />
              ))}
            </div>
          </div>

          {/* 🆕 Phase D2 — mini-tutoriel pour les poignées de redim */}
          <div className="text-xs text-slate-600 bg-blue-50 border border-blue-200 rounded p-2 leading-relaxed">
            💡 <strong>Astuce :</strong> après avoir dessiné une forme (cercle, rectangle, carré, triangle),
            choisis l'outil <strong>↔️ Sélection</strong>, clique sur la forme et glisse les <strong>poignées bleues</strong>
            qui apparaissent autour pour la redimensionner. Tu peux aussi la déplacer en glissant son centre.
          </div>
        </div>

        {/* Canvas */}
        <div className="border-2 border-slate-300 rounded-lg overflow-hidden bg-slate-100 flex items-center justify-center">
          <Stage
            width={canvasWidth}
            height={canvasHeight}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            ref={stageRef}
            className="bg-white"
          >
            <Layer>
              {/* Image de fond */}
              {image && <KonvaImage image={image} width={canvasWidth} height={canvasHeight} />}

              {/* Lignes (crayon) */}
              {lines.map((line, i) => (
                <Line
                  key={i}
                  points={line.points}
                  stroke={line.color}
                  strokeWidth={3}
                  tension={0.5}
                  lineCap="round"
                  lineJoin="round"
                  globalCompositeOperation="source-over"
                />
              ))}

              {/* Formes — 🆕 Phase D2 : draggable + onClick pour sélection + ref pour Transformer */}
              {shapes.map((shape) => {
                const commonProps = {
                  key: shape.id,
                  x: shape.x,
                  y: shape.y,
                  stroke: shape.color,
                  strokeWidth: 3,
                  draggable: true,
                  onClick: () => handleShapeClick(shape.id),
                  onTap: () => handleShapeClick(shape.id),
                  onDragEnd: () => handleShapeTransformEnd(shape.id),
                  onTransformEnd: () => handleShapeTransformEnd(shape.id),
                  ref: (node) => { if (node) shapeRefs.current[shape.id] = node; },
                };
                if (shape.type === 'circle') {
                  return <Circle {...commonProps} radius={shape.radius} />;
                } else if (shape.type === 'rect' || shape.type === 'square') {
                  // 🆕 Phase D2 : 'square' = rect avec init width=height (resize libre ensuite)
                  return (
                    <Rect {...commonProps}
                      width={shape.width}
                      height={shape.height}
                    />
                  );
                } else if (shape.type === 'triangle') {
                  // 🆕 Phase D2 : RegularPolygon (sides=3)
                  return <RegularPolygon {...commonProps} sides={3} radius={shape.radius || 50} />;
                } else if (shape.type === 'text') {
                  return (
                    <KonvaText {...commonProps}
                      text={shape.text}
                      fontSize={shape.fontSize || 24}
                      fill={shape.color}
                      stroke={undefined}
                    />
                  );
                }
                return null;
              })}

              {/* 🆕 Phase D2 — Transformer (poignées bleues de redim) */}
              <Transformer
                ref={transformerRef}
                rotateEnabled={false}
                boundBoxFunc={(oldBox, newBox) => {
                  // Empêche les tailles trop petites (illisibles)
                  if (Math.abs(newBox.width) < 10 || Math.abs(newBox.height) < 10) {
                    return oldBox;
                  }
                  return newBox;
                }}
              />
            </Layer>
          </Stage>
        </div>

        {/* Sélection de zone */}
        <div>
          <Label className="text-sm font-semibold mb-2 block">Zone à créer :</Label>
          <div className="flex flex-wrap gap-2">
            {zones.map((num) => {
              const dispo = zoneEstDispo(num);
              return (
                <button
                  key={num}
                  onClick={() => dispo && setZoneSelectionnee(num)}
                  disabled={!dispo}
                  className={`px-4 py-2 rounded-lg border-2 font-medium transition-all ${
                    zoneSelectionnee === num
                      ? 'bg-blue-600 text-white border-blue-600'
                      : dispo
                      ? 'bg-white text-slate-700 border-slate-300 hover:border-blue-400 hover:bg-blue-50'
                      : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                  }`}
                >
                  Zone {num}
                </button>
              );
            })}
          </div>
          {zonesExistantes.length > 0 && (
            <p className="text-sm text-slate-600 mt-2">
              <strong>Zones existantes :</strong> {zonesExistantes.join(', ')}
            </p>
          )}
        </div>

        {/* 🆕 [5.1.A] Nom de la zone (optionnel) — éditable dès la création */}
        <div>
          <Label className="text-sm font-semibold mb-2 block" htmlFor="zone-nom-input">
            Nom de la zone (optionnel) :
          </Label>
          <input
            id="zone-nom-input"
            type="text"
            value={zoneNom}
            onChange={(e) => setZoneNom(e.target.value)}
            placeholder="Ex: Façade gauche, Garage, Toiture arrière..."
            maxLength={80}
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="zone-photo-nom"
          />
          <p className="text-xs text-slate-500 mt-1">
            Si vide, la zone sera nommée « Zone {zoneSelectionnee || 'N'} ».
          </p>
        </div>

        {/* Description de la zone */}
        <div>
          <Label className="text-sm font-semibold mb-2 block">Description (optionnel) :</Label>
          <textarea
            value={zoneDescription}
            onChange={(e) => setZoneDescription(e.target.value)}
            placeholder="Ex: Façade gauche — gratter peinture écaillée, sabler, 2 couches Sico Cashmere blanc cassé..."
            rows={3}
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm resize-y focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="zone-photo-description"
          />
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t pt-4">
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button
            onClick={handleCreerZone}
            disabled={!zoneSelectionnee || loading}
            className="bg-green-600 hover:bg-green-700"
          >
            {loading ? "Création..." : "✅ Créer la zone"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default EditeurPhotoZone;
